import React, { useRef, useState } from 'react';
import { FiCamera, FiImage, FiX, FiPlus, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const MAKEUP_CATEGORIES = [
  { id: 'foundation',    label: 'Foundation',    emoji: '🟤' },
  { id: 'concealer',     label: 'Concealer',     emoji: '✏️' },
  { id: 'blush',         label: 'Blush',         emoji: '🌸' },
  { id: 'bronzer',       label: 'Bronzer',       emoji: '☀️' },
  { id: 'eyeshadow',     label: 'Eye Shadow',    emoji: '👁️' },
  { id: 'eyeliner',      label: 'Eyeliner',      emoji: '🖊️' },
  { id: 'mascara',       label: 'Mascara',       emoji: '⬛' },
  { id: 'lipColor',      label: 'Lip Color',     emoji: '💋' },
  { id: 'settingSpray',  label: 'Setting Spray', emoji: '💨' },
  { id: 'primer',        label: 'Primer',        emoji: '🧴' },
];

const FACE_AREAS = [
  { id: 'base',   label: 'Base',   emoji: '🟤', placeholder: 'e.g. NARS Sheer Glow, Fenty 260N' },
  { id: 'eyes',   label: 'Eyes',   emoji: '👁️', placeholder: 'e.g. Urban Decay Naked Palette, NYX Liner' },
  { id: 'brows',  label: 'Brows',  emoji: '✍️', placeholder: 'e.g. Anastasia Brow Wiz, Benefit Gimme Brow' },
  { id: 'cheeks', label: 'Cheeks', emoji: '🌸', placeholder: 'e.g. NARS Orgasm Blush, Fenty Bronzer' },
  { id: 'lips',   label: 'Lips',   emoji: '💋', placeholder: 'e.g. MAC Ruby Woo, Charlotte Tilbury Pillow Talk' },
  { id: 'finish', label: 'Finish', emoji: '✨', placeholder: 'e.g. Urban Decay All Nighter, Laura Mercier Powder' },
];

function AreaCard({ area, areaData, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  const photo = areaData?.photo || null;
  const products = areaData?.products || [];
  const hasContent = photo || products.some(p => p.trim());

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => onUpdate({ ...areaData, photo: e.target.result });
    reader.readAsDataURL(file);
    setPickerOpen(false);
  };

  const addProduct = () => onUpdate({ ...areaData, products: [...products, ''] });

  const updateProduct = (idx, val) =>
    onUpdate({ ...areaData, products: products.map((p, i) => (i === idx ? val : p)) });

  const removeProduct = (idx) =>
    onUpdate({ ...areaData, products: products.filter((_, i) => i !== idx) });

  return (
    <div className="glass-card rounded-[2rem] overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{area.emoji}</span>
          <span className="font-sans text-base font-semibold text-slate-900">{area.label}</span>
          {hasContent && <span className="w-2 h-2 rounded-full bg-slate-900 inline-block" />}
        </div>
        <SafeIcon icon={open ? FiChevronUp : FiChevronDown} className="text-slate-400 text-lg" />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">

          {/* Area look photo */}
          <div>
            <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
              Look Photo
            </p>
            {photo ? (
              <div className="relative w-full aspect-video rounded-[1.25rem] overflow-hidden">
                <img src={photo} alt={`${area.label} look`} className="w-full h-full object-cover object-top" />
                <button
                  onClick={() => onUpdate({ ...areaData, photo: null })}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white"
                >
                  <SafeIcon icon={FiX} className="text-xs" />
                </button>
              </div>
            ) : (
              <div>
                {!pickerOpen ? (
                  <button
                    onClick={() => setPickerOpen(true)}
                    className="w-full py-4 rounded-[1.25rem] border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5 active:opacity-70"
                  >
                    <SafeIcon icon={FiCamera} className="text-slate-400 text-xl" />
                    <span className="font-sans text-xs text-slate-400">Add {area.label} look photo</span>
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => cameraRef.current?.click()}
                      className="flex-1 py-3 rounded-[1.25rem] glass-input flex flex-col items-center gap-1 active:scale-95 transition-transform">
                      <SafeIcon icon={FiCamera} className="text-slate-700 text-lg" />
                      <span className="font-sans text-xs text-slate-600">Camera</span>
                    </button>
                    <button onClick={() => uploadRef.current?.click()}
                      className="flex-1 py-3 rounded-[1.25rem] glass-input flex flex-col items-center gap-1 active:scale-95 transition-transform">
                      <SafeIcon icon={FiImage} className="text-slate-700 text-lg" />
                      <span className="font-sans text-xs text-slate-600">Upload</span>
                    </button>
                    <button onClick={() => setPickerOpen(false)}
                      className="w-10 py-3 rounded-[1.25rem] glass-input flex items-center justify-center active:scale-95 transition-transform">
                      <SafeIcon icon={FiX} className="text-slate-500" />
                    </button>
                  </div>
                )}
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
                <input ref={uploadRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
              </div>
            )}
          </div>

          {/* Products for this area */}
          <div>
            <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
              Products Used
            </p>
            <div className="space-y-2">
              {products.map((product, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={product}
                    onChange={e => updateProduct(idx, e.target.value)}
                    placeholder={area.placeholder}
                    className="flex-1 font-sans text-sm text-slate-800 bg-white/60 rounded-2xl px-4 py-2.5 focus:outline-none placeholder:text-slate-300 border border-white/50"
                  />
                  <button onClick={() => removeProduct(idx)}
                    className="w-8 h-8 rounded-full glass-input flex items-center justify-center text-slate-400 active:text-slate-800 flex-shrink-0">
                    <SafeIcon icon={FiX} className="text-sm" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addProduct}
              className="mt-2 flex items-center gap-1.5 font-sans text-sm text-slate-500 active:text-slate-900 transition-colors">
              <SafeIcon icon={FiPlus} className="text-base" />
              Add product
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function StepMakeup({ data, onChange }) {
  const makeup = data.makeup || {
    wore: false,
    products: [],
    doubleCleansed: false,
    notes: '',
    lookPhoto: null,
    areas: {},
  };

  const update = (partial) => onChange({ makeup: { ...makeup, ...partial } });

  const toggleProduct = (id) => {
    const current = makeup.products || [];
    update({
      products: current.includes(id) ? current.filter(p => p !== id) : [...current, id],
    });
  };

  const updateArea = (areaId, areaData) =>
    update({ areas: { ...(makeup.areas || {}), [areaId]: areaData } });

  const [lookPickerOpen, setLookPickerOpen] = useState(false);
  const lookCameraRef = useRef(null);
  const lookUploadRef = useRef(null);

  const handleLookFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => update({ lookPhoto: e.target.result });
    reader.readAsDataURL(file);
    setLookPickerOpen(false);
  };

  return (
    <div className="space-y-6">
      <p className="font-sans text-sm text-slate-500">Log what you wore on your skin today.</p>

      {/* Wore makeup toggle */}
      <div className="glass-card rounded-[2rem] p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-sans text-base font-semibold text-slate-900">Wore makeup today?</p>
            <p className="font-sans text-xs text-slate-400 mt-0.5">Helps correlate skin reactions</p>
          </div>
          <button
            onClick={() => update({ wore: !makeup.wore })}
            className={`relative w-14 h-7 rounded-full transition-all duration-300 ${makeup.wore ? 'bg-slate-900' : 'bg-stone-200'}`}
          >
            <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-all duration-300 ${makeup.wore ? 'translate-x-7' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {makeup.wore && (
        <>
          {/* Category chips */}
          <div>
            <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
              What did you apply?
            </p>
            <div className="flex flex-wrap gap-2.5">
              {MAKEUP_CATEGORIES.map(cat => {
                const active = (makeup.products || []).includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleProduct(cat.id)}
                    className={`rounded-full font-sans text-sm font-medium px-4 py-2.5 transition-all active:scale-95 flex items-center gap-1.5 ${
                      active ? 'bg-slate-900 text-white' : 'glass-input text-slate-700'
                    }`}
                  >
                    <span>{cat.emoji}</span>
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Full look photo */}
          <div>
            <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
              Full Look Photo
            </p>
            {makeup.lookPhoto ? (
              <div className="relative w-full aspect-video rounded-[1.5rem] overflow-hidden">
                <img src={makeup.lookPhoto} alt="full makeup look" className="w-full h-full object-cover object-top" />
                <button
                  onClick={() => update({ lookPhoto: null })}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white"
                >
                  <SafeIcon icon={FiX} className="text-sm" />
                </button>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/40 to-transparent p-4">
                  <p className="font-sans text-xs text-white/80">Full makeup look</p>
                </div>
              </div>
            ) : (
              <div>
                {!lookPickerOpen ? (
                  <button
                    onClick={() => setLookPickerOpen(true)}
                    className="w-full py-6 rounded-[1.5rem] border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 active:opacity-70"
                  >
                    <SafeIcon icon={FiCamera} className="text-slate-400 text-2xl" />
                    <span className="font-sans text-sm text-slate-400">Upload your full makeup look</span>
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => lookCameraRef.current?.click()}
                      className="flex-1 py-4 rounded-[1.5rem] glass-input flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
                      <SafeIcon icon={FiCamera} className="text-slate-700 text-xl" />
                      <span className="font-sans text-xs text-slate-600">Camera</span>
                    </button>
                    <button onClick={() => lookUploadRef.current?.click()}
                      className="flex-1 py-4 rounded-[1.5rem] glass-input flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
                      <SafeIcon icon={FiImage} className="text-slate-700 text-xl" />
                      <span className="font-sans text-xs text-slate-600">Upload</span>
                    </button>
                    <button onClick={() => setLookPickerOpen(false)}
                      className="w-12 rounded-[1.5rem] glass-input flex items-center justify-center active:scale-95 transition-transform">
                      <SafeIcon icon={FiX} className="text-slate-500" />
                    </button>
                  </div>
                )}
                <input ref={lookCameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { handleLookFile(e.target.files?.[0]); e.target.value = ''; }} />
                <input ref={lookUploadRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { handleLookFile(e.target.files?.[0]); e.target.value = ''; }} />
              </div>
            )}
          </div>

          {/* Per-area breakdown */}
          <div>
            <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
              Products by Area
            </p>
            <div className="space-y-3">
              {FACE_AREAS.map(area => (
                <AreaCard
                  key={area.id}
                  area={area}
                  areaData={(makeup.areas || {})[area.id] || {}}
                  onUpdate={(areaData) => updateArea(area.id, areaData)}
                />
              ))}
            </div>
          </div>

          {/* Double cleanse */}
          <div className="glass-card rounded-[2rem] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-sans text-base font-semibold text-slate-900">Double cleansed?</p>
                <p className="font-sans text-xs text-slate-400 mt-0.5">Oil cleanse → water cleanse</p>
              </div>
              <button
                onClick={() => update({ doubleCleansed: !makeup.doubleCleansed })}
                className={`relative w-14 h-7 rounded-full transition-all duration-300 ${makeup.doubleCleansed ? 'bg-slate-900' : 'bg-stone-200'}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-all duration-300 ${makeup.doubleCleansed ? 'translate-x-7' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </>
      )}

      {!makeup.wore && (
        <div className="glass-card rounded-[2rem] p-6 text-center">
          <p className="text-3xl mb-2">💆</p>
          <p className="font-sans text-sm text-slate-500">No-makeup day logged — great for your skin barrier!</p>
        </div>
      )}

      {/* Notes — always visible */}
      <div className="glass-card rounded-[2rem] p-6">
        <label className="block font-sans text-xs font-semibold uppercase tracking-widest text-slate-900 mb-3">
          Makeup Notes
        </label>
        <textarea
          value={makeup.notes || ''}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Products used, skin reaction, brand notes…"
          rows={3}
          className="w-full font-sans text-sm text-slate-800 bg-transparent resize-none focus:outline-none placeholder:text-slate-400"
        />
      </div>
    </div>
  );
}
